function renderFormActionsSearchTemplate({ map }: { map: string }): string {
  return `    const search = filters?.search
    if (search && typeof search === 'string' && search.trim()) {
      const searchTerm = \`%\${search.trim().toLowerCase()}%\`
      conditions.push(
        or(
${map}
        )
      )
    }
`
}

function renderFormActionsComponentTemplate({
  pascal,
  dataFields,
  formName,
  idPlaceholder
}: {
  pascal: string
  dataFields: string
  formName: string
  idPlaceholder: string
}): string {
  return `export interface ${pascal}SubmissionData {
  id: string
${dataFields}
  ipAddress: string | null
  userAgent: string | null
  submittedAt: string
  createdAt: string
  updatedAt: string
}

export interface ${pascal}SubmissionsResponse {
  submissions: ${pascal}SubmissionData[]
  total: number
}

export interface Get${pascal}SubmissionsFilters {
  search?: string
  submittedAtFrom?: string
  submittedAtTo?: string
  limit?: number
  offset?: number
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
}

export interface Create${pascal}SubmissionInput {
${dataFields}
  ipAddress?: string
  userAgent?: string
}

export interface Create${pascal}SubmissionResult {
  success: boolean
  error?: string
  submission?: ${pascal}SubmissionData
}

export interface Delete${pascal}SubmissionResult {
  success: boolean
  error?: string
  count?: number
}

export const CACHE_TAG = '${formName}:submissions:all'
export const CACHE_TAG_BY_ID = (id: string) => \`${formName}:submissions:id:${idPlaceholder}\`
`
}

function renderFormActionsServerActionTemplate({
  sort,
  tableName,
  cacheReadImport,
  pascal,
  exclusiveDateUpperBoundSource,
  sortableColumnsMap,
  cacheLifeLine,
  searchBlock,
  formName
}: {
  sort: string
  tableName: string
  cacheReadImport: string
  pascal: string
  exclusiveDateUpperBoundSource: string
  sortableColumnsMap: string
  cacheLifeLine: string
  searchBlock: string
  formName: string
}): string {
  return `'use server'

import { ${sort} } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
${cacheReadImport}
import { CACHE_TAG } from './types'
import type {
  Get${pascal}SubmissionsFilters,
  ${pascal}SubmissionsResponse,
  ${pascal}SubmissionData
} from './types'

${exclusiveDateUpperBoundSource}

const SORTABLE_COLUMNS = {
${sortableColumnsMap}
} as const

export async function get${pascal}Submissions(
  filters?: Get${pascal}SubmissionsFilters
): Promise<${pascal}SubmissionsResponse> {
  'use cache'${cacheLifeLine}
  cacheTag(CACHE_TAG)

  try {
    const conditions = []
${searchBlock}    if (filters?.submittedAtFrom) {
      conditions.push(gte(${tableName}.submittedAt, filters.submittedAtFrom))
    }
    const submittedAtExclusiveTo = getExclusiveDateUpperBound(filters?.submittedAtTo)
    if (submittedAtExclusiveTo) {
      conditions.push(lt(${tableName}.submittedAt, submittedAtExclusiveTo))
    }

    const query = db.select().from(${tableName})

    const sortColumn = filters?.orderBy && filters.orderBy in SORTABLE_COLUMNS
      ? SORTABLE_COLUMNS[filters.orderBy as keyof typeof SORTABLE_COLUMNS]
      : ${tableName}.submittedAt
    const sortFn =
      filters?.orderBy && filters.orderBy in SORTABLE_COLUMNS
        ? filters.orderDirection === 'desc'
          ? desc
          : asc
        : desc
    const orderClause = sortFn(sortColumn)

    const orderedQuery = conditions.length > 0
      ? query.where(and(...conditions)).orderBy(orderClause)
      : query.orderBy(orderClause)

    const limit = filters?.limit
    const offset = filters?.offset
    const isPaginated = typeof limit === 'number' && limit > 0
    let results: unknown[]
    if (isPaginated) {
      const paginatedQuery = typeof offset === 'number' && offset > 0
        ? orderedQuery.limit(limit).offset(offset)
        : orderedQuery.limit(limit)
      results = await paginatedQuery
    } else {
      results = await orderedQuery
    }

    const total = isPaginated
      ? Number(
          (
            await db
              .select({ count: sql\`count(*)\` })
              .from(${tableName})
              .where(conditions.length > 0 ? and(...conditions) : undefined)
          )[0]?.count ?? 0
        )
      : results.length

    return {
      submissions: results as ${pascal}SubmissionData[],
      total
    }
  } catch (error) {
    console.error('Error fetching ${formName} submissions:', error)
    throw new Error('Failed to fetch ${formName} submissions')
  }
}
`
}

function renderFormActionsServerActionDetailsTemplate({
  tableName,
  cacheReadImport,
  pascal,
  cacheLifeLine,
  formName
}: {
  tableName: string
  cacheReadImport: string
  pascal: string
  cacheLifeLine: string
  formName: string
}): string {
  return `'use server'

import { eq } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
${cacheReadImport}
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
import type { ${pascal}SubmissionData } from './types'

export async function get${pascal}Submission(id: string): Promise<${pascal}SubmissionData | null> {
  'use cache'${cacheLifeLine}
  cacheTag(CACHE_TAG_BY_ID(id))
  cacheTag(CACHE_TAG)

  try {
    const [submission] = await db
      .select()
      .from(${tableName})
      .where(eq(${tableName}.id, id))
      .limit(1)
    return (submission as ${pascal}SubmissionData) ?? null
  } catch (error) {
    console.error(\`Error fetching ${formName} submission \${id}:\`, error)
    throw new Error('Failed to fetch ${formName} submission')
  }
}
`
}

function renderFormActionsServerActionLabelTemplate({
  tableName,
  cacheInvalidationFn,
  mailchimpImport,
  lifecycleHookImport,
  pascal,
  createLifecycleContext,
  beforeHook,
  validation,
  formSettingsKey,
  schemaNotificationFallback,
  formName,
  schemaLabel,
  submitterEmailFieldNull,
  mailchimpCall,
  createResultLifecycleContext,
  afterHook
}: {
  tableName: string
  cacheInvalidationFn: string
  mailchimpImport: string
  lifecycleHookImport: string
  pascal: string
  createLifecycleContext: string
  beforeHook: string
  validation: string
  formSettingsKey: string
  schemaNotificationFallback: string
  formName: string
  schemaLabel: string
  submitterEmailFieldNull: string
  mailchimpCall: string
  createResultLifecycleContext: string
  afterHook: string
}): string {
  return `'use server'

import { getSession } from '@admin/auth/middleware'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
import { getFormSettings } from '@admin/actions/forms'
import { sendFormEmails } from '@admin/actions/email/send-form-emails'
import { getTouchedFieldNames } from '@admin/utils/audit/get-touched-field-names'
import { ${cacheInvalidationFn} } from 'next/cache'
${mailchimpImport}
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
${lifecycleHookImport}
import type { Create${pascal}SubmissionInput, Create${pascal}SubmissionResult, ${pascal}SubmissionData } from './types'

export async function create${pascal}Submission(
  data: Create${pascal}SubmissionInput
): Promise<Create${pascal}SubmissionResult> {
  try {
    const session = await getSession()
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const changedFields = getTouchedFieldNames(data as unknown as Record<string, unknown>)
    const lifecycleContext = ${createLifecycleContext}
    ${beforeHook}

    ${validation}

    const [submission] = await db
      .insert(${tableName})
      .values({
        ...data,
        submittedAt: now
      })
      .returning()
    const created${pascal}Submission = submission as ${pascal}SubmissionData

    const settings = await getFormSettings('${formSettingsKey}')
    const notificationEmails = settings?.notificationEmails
      ? settings.notificationEmails.split(',').map((e: string) => e.trim()).filter(Boolean)
      : ${schemaNotificationFallback}

    await sendFormEmails({
      formName: '${formName}',
      formLabel: '${schemaLabel}',
      notificationEmails,
      submitterEmail: ${submitterEmailFieldNull},
      submissionData: { ...data },
      submittedAt: created${pascal}Submission.submittedAt,
    })${mailchimpCall}

    ${cacheInvalidationFn}(CACHE_TAG)
    ${cacheInvalidationFn}(CACHE_TAG_BY_ID(created${pascal}Submission.id))
    const created${pascal}SubmissionContext = ${createResultLifecycleContext}
    ${afterHook}

    return {
      success: true,
      submission: created${pascal}Submission
    }
  } catch (error) {
    console.error('Error creating ${formName} submission:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create ${formName} submission'
    }
  }
}
`
}

function renderFormActionsServerActionBeforeDeleteTemplate({
  tableName,
  cacheInvalidationFn,
  lifecycleHookImport,
  pascal,
  deleteLifecycleContext,
  beforeHook,
  afterHook,
  formName
}: {
  tableName: string
  cacheInvalidationFn: string
  lifecycleHookImport: string
  pascal: string
  deleteLifecycleContext: string
  beforeHook: string
  afterHook: string
  formName: string
}): string {
  return `'use server'

import { getSession } from '@admin/auth/middleware'
import { eq } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
import { ${cacheInvalidationFn} } from 'next/cache'
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
${lifecycleHookImport}
import type { Delete${pascal}SubmissionResult } from './types'

export async function delete${pascal}Submission(id: string): Promise<Delete${pascal}SubmissionResult> {
  try {
    const session = await getSession()
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const lifecycleContext = ${deleteLifecycleContext}
    ${beforeHook}

    await db.delete(${tableName}).where(eq(${tableName}.id, id))
    ${cacheInvalidationFn}(CACHE_TAG)
    ${cacheInvalidationFn}(CACHE_TAG_BY_ID(id))
    ${afterHook}
    return { success: true }
  } catch (error) {
    console.error(\`Error deleting ${formName} submission \${id}:\`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete ${formName} submission'
    }
  }
}
`
}

function renderFormActionsServerActionBeforeDeleteBranchTemplate({
  tableName,
  cacheInvalidationFn,
  lifecycleHookImport,
  pascal,
  deleteLifecycleContext,
  beforeHook,
  afterHook,
  formName
}: {
  tableName: string
  cacheInvalidationFn: string
  lifecycleHookImport: string
  pascal: string
  deleteLifecycleContext: string
  beforeHook: string
  afterHook: string
  formName: string
}): string {
  return `'use server'

import { getSession } from '@admin/auth/middleware'
import { eq, or } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
import { ${cacheInvalidationFn} } from 'next/cache'
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
${lifecycleHookImport}
import type { Delete${pascal}SubmissionResult } from './types'

export async function deleteBulk${pascal}Submissions(ids: string[]): Promise<Delete${pascal}SubmissionResult> {
  try {
    if (ids.length === 0) return { success: true, count: 0 }
    const session = await getSession()
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const lifecycleContexts = ids.map((id) => (${deleteLifecycleContext}))

    for (const lifecycleContext of lifecycleContexts) {
      ${beforeHook}
    }

    await db.delete(${tableName}).where(
      or(...ids.map(id => eq(${tableName}.id, id)))
    )
    ${cacheInvalidationFn}(CACHE_TAG)
    for (const id of ids) {
      ${cacheInvalidationFn}(CACHE_TAG_BY_ID(id))
    }
    for (const lifecycleContext of lifecycleContexts) {
      ${afterHook}
    }
    return { success: true, count: ids.length }
  } catch (error) {
    console.error(\`Error bulk deleting ${formName} submissions:\`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to bulk delete ${formName} submissions'
    }
  }
}
`
}

function renderFormActionsServerActionDetailsBranchTemplate({
  pascal,
  kebab
}: {
  pascal: string
  kebab: string
}): string {
  return `'use server'

import { get${pascal}Submissions } from './get-${kebab}-submissions'

export async function export${pascal}SubmissionsCSV(): Promise<string> {
  const { submissions } = await get${pascal}Submissions()
  if (submissions.length === 0) return ''
  const headers = Object.keys(submissions[0]).join(',')
  const rows = submissions.map((sub) =>
    Object.values(sub)
      .map((val: unknown) => {
        if (val === null || val === undefined) return ''
        const str = String(val)
        if (str.includes(',') || str.includes('"') || str.includes('\\n')) {
          return \`"\${str.replace(/"/g, '""')}"\`
        }
        return str
      })
      .join(',')
  )
  return [headers, ...rows].join('\\n')
}
`
}

function renderFormActionsContentTemplate({
  pascal,
  kebab,
  exportBarrelLine
}: {
  pascal: string
  kebab: string
  exportBarrelLine: string
}): string {
  return `export type {
  ${pascal}SubmissionData,
  ${pascal}SubmissionsResponse,
  Get${pascal}SubmissionsFilters,
  Create${pascal}SubmissionInput,
  Create${pascal}SubmissionResult,
  Delete${pascal}SubmissionResult
} from './types'
export { get${pascal}Submissions } from './get-${kebab}-submissions'
export { get${pascal}Submission } from './get-${kebab}-submission'
export { create${pascal}Submission } from './create-${kebab}-submission'
export { delete${pascal}Submission } from './delete-${kebab}-submission'
export { deleteBulk${pascal}Submissions } from './delete-bulk-${kebab}-submissions'${exportBarrelLine}
export { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
`
}

export function renderFormActionFiles({
  searchMap,
  pascal,
  dataFields,
  formName,
  idPlaceholder,
  sort,
  tableName,
  cacheReadImport,
  exclusiveDateUpperBoundSource,
  sortableColumnsMap,
  cacheLifeLine,
  cacheInvalidationFn,
  mailchimpImport,
  lifecycleHookImport,
  createLifecycleContext,
  beforeCreateHook,
  validation,
  formSettingsKey,
  schemaNotificationFallback,
  schemaLabel,
  submitterEmailFieldNull,
  mailchimpCall,
  createResultLifecycleContext,
  afterCreateHook,
  deleteLifecycleContext,
  beforeDeleteHook,
  afterDeleteHook,
  kebab,
  exportBarrelLine
}: {
  searchMap: string
  pascal: string
  dataFields: string
  formName: string
  idPlaceholder: string
  sort: string
  tableName: string
  cacheReadImport: string
  exclusiveDateUpperBoundSource: string
  sortableColumnsMap: string
  cacheLifeLine: string
  cacheInvalidationFn: string
  mailchimpImport: string
  lifecycleHookImport: string
  createLifecycleContext: string
  beforeCreateHook: string
  validation: string
  formSettingsKey: string
  schemaNotificationFallback: string
  schemaLabel: string
  submitterEmailFieldNull: string
  mailchimpCall: string
  createResultLifecycleContext: string
  afterCreateHook: string
  deleteLifecycleContext: string
  beforeDeleteHook: string
  afterDeleteHook: string
  kebab: string
  exportBarrelLine: string
}): {
  typesContent: string
  getSubmissionsContent: string
  getSubmissionContent: string
  createContent: string
  deleteContent: string
  deleteBulkContent: string
  exportCsvContent: string
  barrelContent: string
} {
  return {
    typesContent: renderFormActionsComponentTemplate({
      pascal,
      dataFields,
      formName,
      idPlaceholder
    }),
    getSubmissionsContent: renderFormActionsServerActionTemplate({
      sort,
      tableName,
      cacheReadImport,
      pascal,
      exclusiveDateUpperBoundSource,
      sortableColumnsMap,
      cacheLifeLine,
      searchBlock: searchMap ? renderFormActionsSearchTemplate({ map: searchMap }) : '',
      formName
    }),
    getSubmissionContent: renderFormActionsServerActionDetailsTemplate({
      tableName,
      cacheReadImport,
      pascal,
      cacheLifeLine,
      formName
    }),
    createContent: renderFormActionsServerActionLabelTemplate({
      tableName,
      cacheInvalidationFn,
      mailchimpImport,
      lifecycleHookImport,
      pascal,
      createLifecycleContext,
      beforeHook: beforeCreateHook,
      validation,
      formSettingsKey,
      schemaNotificationFallback,
      formName,
      schemaLabel,
      submitterEmailFieldNull,
      mailchimpCall,
      createResultLifecycleContext,
      afterHook: afterCreateHook
    }),
    deleteContent: renderFormActionsServerActionBeforeDeleteTemplate({
      tableName,
      cacheInvalidationFn,
      lifecycleHookImport,
      pascal,
      deleteLifecycleContext,
      beforeHook: beforeDeleteHook,
      afterHook: afterDeleteHook,
      formName
    }),
    deleteBulkContent: renderFormActionsServerActionBeforeDeleteBranchTemplate({
      tableName,
      cacheInvalidationFn,
      lifecycleHookImport,
      pascal,
      deleteLifecycleContext,
      beforeHook: beforeDeleteHook,
      afterHook: afterDeleteHook,
      formName
    }),
    exportCsvContent: renderFormActionsServerActionDetailsBranchTemplate({ pascal, kebab }),
    barrelContent: renderFormActionsContentTemplate({ pascal, kebab, exportBarrelLine })
  }
}
